import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { ApiError, type ExplorerTxRecord, api, isAbortError, totalPages } from '@/shared/api/client'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { formatUTC } from '@/shared/lib/format'
import { isBlockHeight } from '@/shared/lib/validators'
import DetailRow from '@/shared/ui/DetailRow'
import InfoCard from '@/shared/ui/InfoCard'
import ListCard from '@/shared/ui/ListCard'
import { ErrorView, Loading } from '@/shared/ui/StateView'
import Pagination from '@/widgets/pagination/Pagination'
import TxTable from '@/widgets/tx-table/TxTable'

const PAGE_SIZE = 10
const VALIDATOR_NAME = 'YesFi Core'

const parsePageParam = (raw: string | null): number => {
  if (!raw) return 1
  const n = Number.parseInt(raw, 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

const BlockDetails: React.FC = () => {
  const { height } = useParams<{ height: string }>()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = parsePageParam(searchParams.get('page'))
  const [transactions, setTransactions] = useState<ExplorerTxRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryToken, setRetryToken] = useState(0)
  const [totalPageCount, setTotalPageCount] = useState(1)
  const [blockTimestampMs, setBlockTimestampMs] = useState<number | null>(null)

  const blockHeight = (height ?? '').trim()

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (page <= 1) {
            next.delete('page')
          } else {
            next.set('page', String(page))
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  useEffect(() => {
    if (!blockHeight) return
    if (!isBlockHeight(blockHeight)) {
      navigate('/404', { replace: true })
      return
    }

    const ctrl = new AbortController()
    let cancelled = false
    setLoading(true)
    setError(null)

    const tsPromise = api
      .getBlockTimestamp(blockHeight, { signal: ctrl.signal })
      .then((ts) => {
        if (!cancelled) setBlockTimestampMs(ts)
      })
      .catch((err: unknown) => {
        if (cancelled || isAbortError(err)) return
        setBlockTimestampMs(null)
      })

    const listPromise = api
      .getBlock(blockHeight, currentPage, PAGE_SIZE, { signal: ctrl.signal })
      .then((data) => {
        if (cancelled) return
        setTransactions(data.list)
        setTotalPageCount(totalPages(data))
      })
      .catch((err: unknown) => {
        if (cancelled || isAbortError(err)) return
        if (err instanceof ApiError && err.isNotFound) {
          navigate('/404', { replace: true })
          return
        }
        setError(err instanceof Error ? err.message : 'Failed to load block details')
      })

    Promise.allSettled([tsPromise, listPromise]).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [blockHeight, currentPage, navigate, retryToken])

  if (loading) return <Loading />
  if (error) return <ErrorView message={error} onRetry={() => setRetryToken((t) => t + 1)} />

  const fallbackTime = transactions[0]?.block_time
  const timestamp = blockTimestampMs != null ? formatUTC(blockTimestampMs) : formatUTC(fallbackTime)

  const infoCardPadding: React.CSSProperties = {
    padding: isMobile ? 'var(--space-14)' : 'var(--space-3xl)',
    marginBottom: 'var(--space-2xl)',
  }

  return (
    <div style={{ width: '100%' }}>
      <h2
        style={{
          marginBottom: isMobile ? 'var(--space-xl)' : 'var(--space-2xl)',
          fontWeight: 600,
          letterSpacing: 'var(--tracking-title)',
          fontSize: isMobile ? 'var(--font-size-h3)' : 'var(--font-size-h2)',
        }}
      >
        Block Details
      </h2>

      <InfoCard style={infoCardPadding}>
        <DetailRow label="Block Height">
          <span
            style={{
              color: 'var(--app-text-primary)',
              fontSize: isMobile ? 'var(--font-size-md)' : 'var(--font-size-lg)',
              fontWeight: 'bold',
            }}
          >
            {blockHeight}
          </span>
        </DetailRow>
        <DetailRow label="Timestamp">
          <span
            style={{
              color: 'var(--app-text-primary)',
              fontSize: isMobile ? 'var(--font-size-md)' : 'var(--font-size-lg)',
            }}
          >
            {timestamp}
          </span>
        </DetailRow>
        <DetailRow label="Validator" hideDivider>
          <span
            style={{
              color: 'var(--app-text-primary)',
              fontSize: isMobile ? 'var(--font-size-md)' : 'var(--font-size-lg)',
            }}
          >
            {VALIDATOR_NAME}
          </span>
        </DetailRow>
      </InfoCard>

      <ListCard>
        <TxTable
          rows={transactions}
          columns={['hash', 'action', 'block', 'time', 'address', 'amount']}
          emptyText="No transactions in this block."
          columnWidths={{ hash: '16%', action: '22%', block: '10%', time: '19%', address: '18%', amount: '15%' }}
        />
        <Pagination currentPage={currentPage} totalPages={totalPageCount} onPageChange={handlePageChange} />
      </ListCard>
    </div>
  )
}

export default BlockDetails
